/**
 * Centralized React Query Key Factory
 *
 * Hierarchical key structure enabling granular or broad cache invalidation:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.transactions.all })
 *     → invalidates ALL transaction queries
 *   queryClient.invalidateQueries({ queryKey: queryKeys.transactions.detail('123') })
 *     → invalidates only one transaction
 *
 * Pattern: all → lists → list(params) → detail(id)
 */

declare const editSeedBrand: unique symbol

/**
 * The key of the fresh read an edit form is seeded from (`FreshEditSeedGate`). It carries a brand only
 * `queryKeys.editSeed` can give, so a literal `['edit-seed', 'x']` or a key of a list or a screen does not compile
 * (its `gcTime: 0` / always-refetch rules belong to the gate alone).
 */
export type EditSeedKey = readonly ['edit-seed', string, string, ...string[]] & {
  readonly [editSeedBrand]: true
}

export const queryKeys = {
  /**
   * One fresh read of ONE subject for its edit form: the `scope` names the kind (`'pricing-plan'`), `id` the subject
   * (and `rest` the rest of its identity). The key is the identity of the subject in the gate, so an empty `id` (two
   * subjects sharing one seed) is a bug of the caller and throws.
   */
  editSeed: (scope: string, id: string, ...rest: string[]): EditSeedKey => {
    if (!scope || !id || rest.includes('')) {
      throw new Error(
        `queryKeys.editSeed needs a non-empty scope and ids, got [${[scope, id, ...rest].join(', ')}]`,
      )
    }
    return ['edit-seed', scope, id, ...rest] as unknown as EditSeedKey
  },

  // ------------------------------------------------------------------
  // Config & system (assets, exchanges, validation rules, categories)
  // ------------------------------------------------------------------
  config: {
    all: ['config'] as const,
    app: () => [...queryKeys.config.all, 'app'] as const,
    features: () => [...queryKeys.config.all, 'features'] as const,
    assets: () => [...queryKeys.config.all, 'assets'] as const,
    assetCategories: () => [...queryKeys.config.all, 'asset-categories'] as const,
    exchanges: () => [...queryKeys.config.all, 'exchanges'] as const,
    validationRules: () => [...queryKeys.config.all, 'validation-rules'] as const,
    profileOptions: () => [...queryKeys.config.all, 'profile-options'] as const,
    integrationSources: () => [...queryKeys.config.all, 'integration-sources'] as const,
    version: () => [...queryKeys.config.all, 'version'] as const,
  },

  // ------------------------------------------------------------------
  // Transactions (core accounting entity)
  // ------------------------------------------------------------------
  transactions: {
    all: ['transactions'] as const,
    lists: () => [...queryKeys.transactions.all, 'list'] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.transactions.lists(), params] as const,
    detail: (id: string) => [...queryKeys.transactions.all, 'detail', id] as const,
    count: (params?: Record<string, unknown>) =>
      [...queryKeys.transactions.all, 'count', params] as const,
    attributes: (locale?: string) => [...queryKeys.transactions.all, 'attributes', locale] as const,
    infiniteLists: () => [...queryKeys.transactions.all, 'infinite'] as const,
    infiniteList: (filters?: Record<string, unknown>) =>
      [...queryKeys.transactions.infiniteLists(), filters ?? null] as const,
  },

  // ------------------------------------------------------------------
  // Assets (tenant-specific assets, search)
  // ------------------------------------------------------------------
  assets: {
    all: ['assets'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.assets.all, 'list', params] as const,
    search: (query: string) => [...queryKeys.assets.all, 'search', query] as const,
    detail: (id: string) => [...queryKeys.assets.all, 'detail', id] as const,
  },

  // ------------------------------------------------------------------
  // Balances
  // ------------------------------------------------------------------
  balances: {
    all: ['balances'] as const,
    lists: () => [...queryKeys.balances.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.balances.lists(), params] as const,
    stats: () => [...queryKeys.balances.all, 'stats'] as const,
  },

  // ------------------------------------------------------------------
  // Imports: data sources (wallets), jobs, templates, mappings, chains
  // ------------------------------------------------------------------
  imports: {
    all: ['imports'] as const,

    // Data sources / wallets
    sources: () => [...queryKeys.imports.all, 'sources'] as const,
    source: (id: string) => [...queryKeys.imports.sources(), id] as const,
    /** Only the connection STATE of a source (a string); `source(id)` is reserved for the full source. */
    sourceStatus: (id: string) => [...queryKeys.imports.source(id), 'status'] as const,
    sourceCrossChain: (id: string) => [...queryKeys.imports.source(id), 'cross-chain'] as const,
    sourceTransactions: (id: string, params?: Record<string, unknown>) =>
      [...queryKeys.imports.source(id), 'transactions', params] as const,

    // Jobs
    jobs: () => [...queryKeys.imports.all, 'jobs'] as const,
    job: (id: string) => [...queryKeys.imports.jobs(), id] as const,

    // CSV / exchange templates & mappings
    templates: () => [...queryKeys.imports.all, 'templates'] as const,
    mappings: () => [...queryKeys.imports.all, 'mappings'] as const,

    // Supported chains
    chains: () => [...queryKeys.imports.all, 'chains'] as const,
  },

  // ------------------------------------------------------------------
  // Tax Reports
  // ------------------------------------------------------------------
  taxReports: {
    all: ['tax-reports'] as const,
    lists: () => [...queryKeys.taxReports.all, 'list'] as const,
    list: (params?: Record<string, unknown>) => [...queryKeys.taxReports.lists(), params] as const,
    detail: (id: string) => [...queryKeys.taxReports.all, 'detail', id] as const,
    lots: (reportId: string) => [...queryKeys.taxReports.all, 'lots', reportId] as const,
    availableYears: () => [...queryKeys.taxReports.all, 'available-years'] as const,
    assets: (reportId: string, sortBy?: string) =>
      [...queryKeys.taxReports.all, 'assets', reportId, sortBy] as const,
    calculations: (reportId: string, filters?: Record<string, unknown>) =>
      [...queryKeys.taxReports.all, 'calculations', reportId, filters] as const,
    attributions: (reportId: string, calculationId: string) =>
      [...queryKeys.taxReports.all, 'attributions', reportId, calculationId] as const,
  },

  // ------------------------------------------------------------------
  // Transaction Matching (bridges, suggestions)
  // ------------------------------------------------------------------
  matching: {
    all: ['matching'] as const,
    suggestions: (params?: Record<string, unknown>) =>
      [...queryKeys.matching.all, 'suggestions', params] as const,
    bridges: () => [...queryKeys.matching.all, 'bridges'] as const,
  },

  // ------------------------------------------------------------------
  // Platforms / Exchanges (integration registry)
  // ------------------------------------------------------------------
  platforms: {
    all: ['platforms'] as const,
    list: () => [...queryKeys.platforms.all, 'list'] as const,
    exchanges: () => [...queryKeys.platforms.all, 'exchanges'] as const,
  },

  // ------------------------------------------------------------------
  // User profile + customer-scoped resources (tickets, etc.)
  // ------------------------------------------------------------------
  user: {
    all: ['user'] as const,
    profile: () => [...queryKeys.user.all, 'profile'] as const,
    preferences: () => [...queryKeys.user.all, 'preferences'] as const,

    // Customer-facing support tickets
    tickets: {
      all: ['user', 'tickets'] as const,
      lists: () => [...queryKeys.user.tickets.all, 'list'] as const,
      list: (params?: Record<string, unknown>) =>
        [...queryKeys.user.tickets.lists(), params] as const,
      detail: (id: string) => [...queryKeys.user.tickets.all, 'detail', id] as const,
    },
  },

  // ------------------------------------------------------------------
  // Admin — super-admin features
  // ------------------------------------------------------------------
  admin: {
    // Tenants
    tenants: {
      all: ['admin', 'tenants'] as const,
      lists: () => [...queryKeys.admin.tenants.all, 'list'] as const,
      list: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.tenants.lists(), params] as const,
      detail: (id: string) => [...queryKeys.admin.tenants.all, 'detail', id] as const,
      /** The screen `admin.tenants` («Organizaciones»): its counts, list, plan catalogue and impersonation sessions. */
      screen: ['screen', 'admin.tenants'] as const,
    },

    // System users
    users: {
      all: ['admin', 'users'] as const,
      /** The screen `admin.users` («Usuarios»): its counts, one page of users and the super admins. */
      screen: ['screen', 'admin.users'] as const,
      lists: () => [...queryKeys.admin.users.all, 'list'] as const,
      list: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.users.lists(), params] as const,
      detail: (id: string) => [...queryKeys.admin.users.all, 'detail', id] as const,
      stats: () => [...queryKeys.admin.users.all, 'stats'] as const,
      sessions: (id: string) => [...queryKeys.admin.users.all, 'sessions', id] as const,
      loginHistory: (id: string) => [...queryKeys.admin.users.all, 'login-history', id] as const,
      activity: (id: string) => [...queryKeys.admin.users.all, 'activity', id] as const,
      auditLog: (id: string) => [...queryKeys.admin.users.all, 'audit-log', id] as const,
      notes: (id: string) => [...queryKeys.admin.users.all, 'notes', id] as const,
      resourceUsage: (id: string) => [...queryKeys.admin.users.all, 'resource-usage', id] as const,
      gdpr: (id: string) => [...queryKeys.admin.users.all, 'gdpr', id] as const,
    },

    // Revenue
    revenue: {
      /** The screen `admin.revenue` («Ingresos»); the period travels in the key. */
      screen: ['screen', 'admin.revenue'] as const,
    },

    // Blockchain templates
    blockchainTemplates: {
      all: ['admin', 'blockchain-templates'] as const,
      lists: () => [...queryKeys.admin.blockchainTemplates.all, 'list'] as const,
      list: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.blockchainTemplates.lists(), params] as const,
      detail: (id: string) => [...queryKeys.admin.blockchainTemplates.all, 'detail', id] as const,
      unknownContracts: () =>
        [...queryKeys.admin.blockchainTemplates.all, 'unknown-contracts'] as const,
    },

    // Market data
    marketData: {
      all: ['admin', 'market-data'] as const,
      prices: (params?: Record<string, unknown>) =>
        [...queryKeys.admin.marketData.all, 'prices', params] as const,
      /** The screen `admin.marketData.inventory` («Activos cripto»); the query travels in the key. */
      inventoryScreen: ['screen', 'admin.marketData.inventory'] as const,
      /** The screen `admin.marketData.inventoryFx` («Divisas FX»); the query travels in the key. */
      inventoryFxScreen: ['screen', 'admin.marketData.inventoryFx'] as const,
      /** The screen `admin.marketData.inventoryConversions` («Conversiones»). */
      inventoryConversionsScreen: ['screen', 'admin.marketData.inventoryConversions'] as const,
      /** The screen `admin.marketData.download` («Descarga»); the source asked for travels in the key. */
      downloadScreen: ['screen', 'admin.marketData.download'] as const,
      /** The screen `admin.marketData.jobs` («Trabajos»); the query travels in the key. */
      jobsScreen: ['screen', 'admin.marketData.jobs'] as const,
    },

    // Email
    email: {
      all: ['admin', 'email'] as const,
      templates: () => [...queryKeys.admin.email.all, 'templates'] as const,
      logs: (filter?: Record<string, unknown>) =>
        [...queryKeys.admin.email.all, 'logs', filter] as const,
      logDetail: (id: string) => [...queryKeys.admin.email.all, 'log-detail', id] as const,
      stats: () => [...queryKeys.admin.email.all, 'stats'] as const,
      enhancedStats: (period?: string) =>
        [...queryKeys.admin.email.all, 'enhanced-stats', period] as const,
      templateAnalytics: (period?: string) =>
        [...queryKeys.admin.email.all, 'template-analytics', period] as const,
      engagementTrend: (period?: string) =>
        [...queryKeys.admin.email.all, 'engagement-trend', period] as const,
      suppressions: () => [...queryKeys.admin.email.all, 'suppressions'] as const,
      settings: () => [...queryKeys.admin.email.all, 'settings'] as const,
      /** The screen `admin.email.logs` («Correo › Registro»); the query travels in the key. */
      logsScreen: ['screen', 'admin.email.logs'] as const,
      /** The screen `admin.email.settings` («Correo › Proveedor»): the platform provider view. */
      settingsScreen: ['screen', 'admin.email.settings'] as const,
      /** The screen `admin.email.templates` («Correo › Plantillas»): every template. */
      templatesScreen: ['screen', 'admin.email.templates'] as const,
    },

    // Jobs (super-admin screen «Trabajos», #2891)
    jobs: {
      all: ['admin', 'jobs'] as const,
      /** The screen `admin.jobs` («Trabajos»); the query travels in the key. */
      screen: ['screen', 'admin.jobs'] as const,
      detail: (id: string) => [...queryKeys.admin.jobs.all, 'detail', id] as const,
    },

    // System status (super-admin screen «Estado del sistema», #2893): three independent reads
    systemStatus: {
      all: ['admin', 'system-status'] as const,
      health: () => [...queryKeys.admin.systemStatus.all, 'health'] as const,
      metrics: () => [...queryKeys.admin.systemStatus.all, 'metrics'] as const,
      external: () => [...queryKeys.admin.systemStatus.all, 'external'] as const,
    },

    // Tenant Impersonation
    impersonation: {
      _root: ['admin', 'impersonation'] as const,
      status: (scope: string) => [...queryKeys.admin.impersonation._root, 'status', scope] as const,
    },

    // Recovery posture («Recuperación»)
    recovery: {
      posture: ['admin', 'recovery'] as const,
    },

    /** The screen `admin.rateLimiting` («Límites de uso»); the query travels in the key. */
    rateLimiting: { screen: ['screen', 'admin.rateLimiting'] as const },
  },
} as const
